import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";
import type {
  CotizacionCreate,
  CotizacionUpdate,
  EstadoCotizacion,
  FacturarCotizacion,
  Moneda,
} from "@uirtus/shared";
import { api, apiBlob } from "./api";
import type { Cliente } from "./clientes";
import type { Proyecto } from "./proyectos";

export interface ConceptoCotizacion {
  id: string;
  cotizacionId: string;
  servicioId: string | null;
  orden: number;
  claveProdServ: string;
  claveUnidad: string;
  unidad: string | null;
  noIdentificacion: string | null;
  descripcion: string;
  cantidad: string;
  valorUnitario: string;
  descuento: string;
  importe: string;
  objetoImp: string;
  tasaIva: string | null;
  tasaRetIva: string | null;
  tasaRetIsr: string | null;
  trasladoIva: string | null;
  retencionIva: string | null;
  retencionIsr: string | null;
}

export interface Cotizacion {
  id: string;
  folio: string;
  version: number;
  cotizacionPadreId: string | null;
  clienteId: string;
  proyectoId: string | null;
  titulo: string;
  estado: EstadoCotizacion;
  moneda: Moneda;
  tipoCambio: string | null;
  validaHasta: string;
  condicionesDePago: string | null;
  notas: string | null;
  subtotal: string;
  descuento: string;
  impuestosTrasladados: string;
  impuestosRetenidos: string;
  total: string;
  enviadaAt: string | null;
  resueltaAt: string | null;
  motivoResolucion: string | null;
  creadoAt: string;
}

export interface CotizacionLista extends Cotizacion {
  cliente: { id: string; rfc: string; razonSocial: string };
}

/** Una entrada del historial de versiones del mismo folio. */
export interface VersionResumen {
  id: string;
  version: number;
  estado: EstadoCotizacion;
  total: string;
  creadoAt: string;
}

export interface CotizacionConDetalle extends Cotizacion {
  cliente: Cliente;
  proyecto: Proyecto | null;
  conceptos: ConceptoCotizacion[];
  /** Todas las versiones del mismo folio, para poder saltar entre ellas. */
  versiones: VersionResumen[];
}

const CLAVE = ["cotizaciones"] as const;

export interface FiltrosCotizaciones {
  clienteId?: string;
  estado?: EstadoCotizacion;
  limite?: number;
}

export function useCotizaciones(filtros: FiltrosCotizaciones = {}) {
  const parametros = new URLSearchParams();
  if (filtros.clienteId) parametros.set("clienteId", filtros.clienteId);
  if (filtros.estado) parametros.set("estado", filtros.estado);
  parametros.set("limite", String(filtros.limite ?? 200));

  return useQuery({
    queryKey: [...CLAVE, filtros],
    queryFn: () => api<CotizacionLista[]>(`/cotizaciones?${parametros.toString()}`),
  });
}

export function useCotizacion(id: string) {
  return useQuery({
    queryKey: [...CLAVE, id],
    queryFn: () => api<CotizacionConDetalle>(`/cotizaciones/${id}`),
    enabled: !!id,
  });
}

function useMutacionCotizacion<TDatos, TVars>(
  fn: (vars: TVars) => Promise<TDatos>,
  opciones?: UseMutationOptions<TDatos, Error, TVars>,
) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: fn,
    ...opciones,
    onSuccess: (...args) => {
      void cliente.invalidateQueries({ queryKey: CLAVE });
      opciones?.onSuccess?.(...args);
    },
  });
}

export const useCrearCotizacion = () =>
  useMutacionCotizacion((body: CotizacionCreate) =>
    api<Cotizacion>("/cotizaciones", { method: "POST", body }),
  );

export const useActualizarCotizacion = (id: string) =>
  useMutacionCotizacion((body: CotizacionUpdate) =>
    api<Cotizacion>(`/cotizaciones/${id}`, { method: "PATCH", body }),
  );

export const useCambiarEstadoCotizacion = (id: string) =>
  useMutacionCotizacion((body: { estado: EstadoCotizacion; motivo?: string }) =>
    api<Cotizacion>(`/cotizaciones/${id}/estado`, { method: "POST", body }),
  );

/** Renegociar: crea una versión nueva del mismo folio, siempre en borrador. */
export const useNuevaVersionCotizacion = (id: string) =>
  useMutacionCotizacion(() =>
    api<Cotizacion>(`/cotizaciones/${id}/version`, { method: "POST" }),
  );

/** Genera la factura en borrador a partir de una cotización aceptada. */
export const useFacturarCotizacion = (id: string) =>
  useMutacionCotizacion((body: FacturarCotizacion) =>
    api<{ id: string }>(`/cotizaciones/${id}/facturar`, { method: "POST", body }),
  );

export const useEliminarCotizacion = (id: string) =>
  useMutacionCotizacion(() => api<void>(`/cotizaciones/${id}`, { method: "DELETE" }));

export const obtenerPdfCotizacion = (id: string): Promise<Blob> =>
  apiBlob(`/cotizaciones/${id}/pdf`);
